<script lang="ts" setup>
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue'
import type { SelectOptions } from '~~/types/components/default'

const props = defineProps<{
  isMultiple: boolean
  options: SelectOptions[]
  model: SelectOptions | SelectOptions[] | undefined
  placeholder: string
}>()

const emit = defineEmits(['updateModel'])

const selectedText = computed(() => {
  const { model, placeholder } = props
  if (Array.isArray(model)) {
    return model.map(({ label }) => label).join(', ') || placeholder
  }
  if (typeof model === 'object') return model.label
  return placeholder
})
</script>

<template lang="pug">
Listbox(
  as="div"
  :modelValue="model"
  :multiple="isMultiple"
  @update:modelValue="(value) => emit('updateModel', value)"
  class="grow shrink max-w-xl min-w-0"
)
  div(class="relative")
    ListboxButton(
      class="relative w-full text-lg md:text-xl font-medium cursor-pointer pt-6 pb-9 px-12 pr-6 text-left border-b border-grey-300 focus:outline-none focus-visible:border-highlight focus-visible:ring-1 focus-visible:ring-highlight"
    )
      span(class="flex items-center flex justify-between items-center gap-12")
        span(class="block truncate") {{ selectedText }}
        MaterialSymbol(class="pointer-events-none flex-shrink-0") expand_more

    Transition(
      leave-active-class="transition duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    )
      ListboxOptions(
        class="absolute m-0 p-0 py-9 left-0 top-full text-base max-h-320 w-full overflow-auto bg-grey-100 shadow-2xl z-30 focus:outline-none focus-visible:border-highlight focus-visible:ring-1 focus-visible:ring-highlight"
      )
        ListboxOption(
          v-for="option in options"
          v-slot="{ selected }"
          :key="option.value"
          :value="option"
          as="template"
        )
          li(
            class="cursor-pointer relative m-0 select-none py-9 pl-12 pr-12 hover:text-blue-dark focus:outline-none focus-visible:text-dark-blue focus-visible:bg-highlight"
            :class="[selected ? 'text-blue-dark' : 'text-grey-500']"
          )
            span(
              class="inline-block relative text-md md:text-lg font-normal after:opacity-0 after:absolute after:top-full after:left-0 after:bg-highlight after:h-3 after:w-full"
              :class="[selected ? 'after:opacity-100' : '']"
            ) {{ option.label }}
</template>
